import { reducer as storeReducer } from 'store/common';
import ActionType from './actionType';
import {
  getDaysInOfficeRatio,
  lastHeadcount,
  initGrowth,
  updateGrowthParams,
  updateGrowthStartingYear,
  updateGrowthLeaseTermInYears,
  updateGrowthDefaultChange,
  updateGrowthChange,
  updateGrowthHeadcount,
} from 'services/growth/growth';
import {
  getInsiteWorkplaces,
  updateWorkplaceSpaces,
  updateSpaceType,
  updateCustomSpace,
  removeCustomSpace,
  updateSettings,
  updateWorkPlaces,
} from 'services/inSite/inSite';
import { getCountry } from 'utils/country';
import countries from 'constants/countries';
import getDefaultSharedSeatsRatio from 'services/inSite/sharedSeatsRatio';

const defaultLeaseTermInYears = {
  [countries.US]: 5,
  [countries.UK]: 5,
  [countries.FR]: 6,
  [countries.EU]: 5,
  [countries.DE]: 5,
};
const defaultCountry = countries.US;
const defaultHeadcount = 0;
const defaultInOffice = 0;
const defaultFullyRemote = 0;
const defaultHybrid = 0;
const defaultDaysInOffice = 1;
const defaultGrowthValue = 5;
const defaultWorkplaceSelected = 2;

const initialState = (date = new Date(), country = getCountry() || defaultCountry) => {
  const initialGrowth = initGrowth({
    startingYear: date.getFullYear(),
    years: defaultLeaseTermInYears[country],
    headcount: defaultHeadcount,
    officeRatio: defaultInOffice,
    remoteRatio: defaultFullyRemote,
    hybridRatio: defaultHybrid,
    daysInOffice: defaultDaysInOffice,
    defaultGrowth: defaultGrowthValue,
    defaultSharedSeatsRatio: getDefaultSharedSeatsRatio(country),
  });

  const initialSpaces = getInsiteWorkplaces(
    lastHeadcount(initialGrowth),
    defaultFullyRemote,
    defaultHybrid,
    getDaysInOfficeRatio(defaultDaysInOffice, getDefaultSharedSeatsRatio(country)),
    country,
    defaultInOffice,
    defaultHeadcount,
    getDefaultSharedSeatsRatio(country),
  );

  return {
    id: 0,
    leaseExpirationYear: null,
    size: null,
    startingYear: date.getTime(),
    leaseTermInYears: defaultLeaseTermInYears[country],
    headcount: defaultHeadcount,
    inOffice: defaultInOffice,
    fullyRemote: defaultFullyRemote,
    hybrid: defaultHybrid,
    daysInOffice: defaultDaysInOffice,
    defaultGrowth: defaultGrowthValue,
    growth: initialGrowth,
    spaces: initialSpaces,
    workplacestyleSelected: defaultWorkplaceSelected,
    country: country,
    name: '',
    createdAt: null,
    sharedUsers: [],
    owner: {},
    anyonecanaccess: false,
    scoreCardReached: false,
    lastStep: 1,
    sharedSeatsRatio: getDefaultSharedSeatsRatio(country),
  };
};

// TO-DO. For all the reducers => refactor and extract common logic to calculate growth/spaces.
const updateId = (state, payload) => ({
  ...state,
  id: payload.id,
});

const updateCurrentSituation = (state, payload) => ({
  ...state,
  leaseExpirationYear: payload.leaseExpirationYear,
  size: payload.size,
});

const updateParams = (state, payload) => {
  const headcount = payload.headcount;
  const inOffice = payload.inOffice;
  const fullyRemote = payload.fullyRemote;
  const hybrid = payload.hybrid;
  const daysInOffice = payload.daysInOffice;

  const growth = updateGrowthParams(
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    state.growth,
    state.sharedSeatsRatio,
  );
  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    fullyRemote,
    hybrid,
    getDaysInOfficeRatio(daysInOffice, state.sharedSeatsRatio),
    getCountry(),
    inOffice,
    headcount,
  );

  return {
    ...state,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    growth,
    spaces,
  };
};

const updateTotalHeadcount = (state, payload) => {
  const headcount = payload.headcount;
  const growth = updateGrowthParams(
    payload.headcount,
    state.inOffice,
    state.fullyRemote,
    state.hybrid,
    state.daysInOffice,
    state.growth,
    state.sharedSeatsRatio,
  );
  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
    state.inOffice,
    headcount,
  );

  return {
    ...state,
    headcount,
    growth,
    spaces,
  };
};

const updateStartingYear = (state, payload) => ({
  ...state,
  startingYear: payload.startingYear,
  growth: updateGrowthStartingYear(new Date(payload.startingYear).getFullYear(), state.growth),
});

const updateLeaseTermInYears = (state, payload) => {
  const growth = updateGrowthLeaseTermInYears(
    payload.leaseTermInYears,
    state.inOffice,
    state.fullyRemote,
    state.hybrid,
    state.daysInOffice,
    state.defaultGrowth,
    state.growth,
    state.sharedSeatsRatio,
  );
  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
    state.inOffice,
    state.headcount,
  );

  return {
    ...state,
    leaseTermInYears: payload.leaseTermInYears,
    growth,
    spaces,
  };
};

const updateDefaultGrowth = (state, payload) => {
  const growth = updateGrowthDefaultChange(
    payload.defaultGrowth,
    state.headcount,
    state.inOffice,
    state.fullyRemote,
    state.hybrid,
    state.daysInOffice,
    state.growth,
    state.sharedSeatsRatio,
  );

  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
    state.inOffice,
    state.headcount,
  );

  return {
    ...state,
    defaultGrowth: payload.defaultGrowth,
    growth,
    spaces,
  };
};

const updateChange = (state, payload) => {
  const growth = updateGrowthChange({
    newChange: payload.change,
    changeIndex: payload.index,
    headcount: state.headcount,
    officeRatio: state.inOffice,
    remoteRatio: state.fullyRemote,
    hybridRatio: state.hybrid,
    daysInOffice: state.daysInOffice,
    growth: state.growth,
    sharedSeatsRatio: state.sharedSeatsRatio,
  });

  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
    state.inOffice,
    state.headcount,
  );

  return {
    ...state,
    growth,
    spaces,
  };
};

const updateHeadcount = (state, payload) => {
  const growth = updateGrowthHeadcount({
    newHeadcount: payload.headcount,
    changeIndex: payload.index,
    headcount: state.headcount,
    officeRatio: state.inOffice,
    remoteRatio: state.fullyRemote,
    hybridRatio: state.hybrid,
    daysInOffice: state.daysInOffice,
    growth: state.growth,
    sharedSeatsRatio: state.sharedSeatsRatio,
  });

  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
    state.inOffice,
    state.headcount,
  );

  return {
    ...state,
    growth,
    spaces,
  };
};

const updateRatios = (state, payload) => {
  const sharedSeatsRatio = payload.sharedSeatsRatio;
  const growth = updateGrowthParams(
    state.headcount,
    state.inOffice,
    state.fullyRemote,
    state.hybrid,
    state.daysInOffice,
    state.growth,
    sharedSeatsRatio,
  );
  const spaces = getInsiteWorkplaces(
    lastHeadcount(growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, sharedSeatsRatio),
    getCountry(),
    state.inOffice,
    state.headcount,
  );
  return {
    ...state,
    spaces,
    growth,
    sharedSeatsRatio,
  };
};

const updateWorkplaceStyleSelected = (state, payload) => {
  const workplacestyleSelected = payload.workplacestyleSelected;
  return {
    ...state,
    workplacestyleSelected,
  };
};

const updateSpaces = (state, payload) => {
  const newSpaces = updateSpaceType(state.spaces, state.workplacestyleSelected, payload.spaceType);

  const spaces = updateWorkplaceSpaces(
    newSpaces,
    lastHeadcount(state.growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
  );

  return {
    ...state,
    spaces,
  };
};

const updateSpaceSettings = (state, payload) => {
  const spaces = updateSettings(state.spaces, payload);

  updateWorkPlaces(
    spaces,
    lastHeadcount(state.growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
  );

  return {
    ...state,
    spaces,
  };
};

const updateCustomSpaces = (state, payload) => {
  const newSpaces = updateCustomSpace(
    state.spaces,
    state.workplacestyleSelected,
    payload.customSpace,
  );

  const spaces = updateWorkplaceSpaces(
    newSpaces,
    lastHeadcount(state.growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
  );

  return {
    ...state,
    spaces,
  };
};

const updateLastStep = (state, payload) => {
  const lastStep = payload.lastStep;

  return {
    ...state,
    lastStep,
  };
};

const updateScorecardReached = (state, payload) => {
  const scoreCardReached = payload.scoreCardReached;

  return {
    ...state,
    scoreCardReached,
  };
};

const removeCustomSpaces = (state, payload) => {
  const newSpaces = removeCustomSpace(
    state.spaces,
    state.workplacestyleSelected,
    payload.customSpace,
  );

  const spaces = updateWorkplaceSpaces(
    newSpaces,
    lastHeadcount(state.growth),
    state.fullyRemote,
    state.hybrid,
    getDaysInOfficeRatio(state.daysInOffice, state.sharedSeatsRatio),
    getCountry(),
  );

  return {
    ...state,
    spaces,
  };
};

const setNA = (state, payload) => {
  let {
    id,
    leaseExpiration,
    size,
    startingYear,
    leaseTermYears,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    defaultGrowth,
    workplaceStyleSelected,
    workplaces,
    growth,
    name,
    companyName,
    createdAt,
    sharedUsers,
    owner,
    anyoneCanAccess,
    country,
    lastStep,
    scoreCardReached,
    sharedSeatsRatio,
    isExternal,
  } = payload.na;

  // If NA was not yet initialized, i.e it was created from Journeys.
  // Load default growth and workplaces information.
  if (workplaces === '[]' || growth === '[]' || sharedSeatsRatio === '[]') {
    const initials = initialState(new Date(startingYear), country);

    leaseTermYears = initials.leaseTermInYears;
    headcount = initials.headcount;
    inOffice = initials.inOffice;
    fullyRemote = initials.fullyRemote;
    hybrid = initials.hybrid;
    daysInOffice = initials.daysInOffice;
    defaultGrowth = initials.defaultGrowth;
    workplaceStyleSelected = initials.workplacestyleSelected;
    growth = JSON.stringify(initials.growth);
    workplaces = JSON.stringify(initials.spaces);
    lastStep = 1;
    scoreCardReached = false;
    sharedSeatsRatio = JSON.stringify(initials.sharedSeatsRatio);
  }

  return {
    ...state,
    id,
    leaseExpirationYear: leaseExpiration ? Date.parse(leaseExpiration) : null,
    size,
    startingYear: Date.parse(startingYear),
    leaseTermInYears: leaseTermYears,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    defaultGrowth,
    workplacestyleSelected: workplaceStyleSelected,
    growth: JSON.parse(growth),
    spaces: JSON.parse(workplaces),
    name,
    companyName: companyName,
    createdAt,
    sharedUsers,
    owner,
    country,
    anyonecanaccess: anyoneCanAccess,
    lastStep: lastStep,
    scoreCardReached: scoreCardReached,
    isExternal: isExternal,
    sharedSeatsRatio: JSON.parse(sharedSeatsRatio),
  };
};

const resetNA = (state, _) => ({ ...state, ...initialState() });

const addSharedUser = (state, payload) => {
  const sharedUsers = state.sharedUsers.some(
    (shareduser) => shareduser.id === payload.shareduser.id,
  )
    ? [...state.sharedUsers]
    : [...state.sharedUsers, payload.shareduser];

  return {
    ...state,
    sharedUsers,
  };
};

const removeSharedUser = (state, payload) => {
  const sharedUsers = state.sharedUsers.filter(
    (shareduser) => shareduser.id !== payload.shareduser.id,
  );

  return {
    ...state,
    sharedUsers,
  };
};

const setAnyoneCanAccess = (state, payload) => {
  const anyonecanaccess = payload.anyonecanaccess;

  return {
    ...state,
    anyonecanaccess,
  };
};

const actionsMapping = {
  [ActionType.UPDATE_NA_ID]: updateId,
  [ActionType.UPDATE_CURRENT_SITUATION]: updateCurrentSituation,
  [ActionType.UPDATE_PARAMS]: updateParams,
  [ActionType.UPDATE_TOTAL_HEADCOUNT]: updateTotalHeadcount,
  [ActionType.UPDATE_STARTINGYEAR]: updateStartingYear,
  [ActionType.UPDATE_LEASETERMINYEARS]: updateLeaseTermInYears,
  [ActionType.UPDATE_DEFAULTGROWTH]: updateDefaultGrowth,
  [ActionType.UPDATE_GROWTH_CHANGE]: updateChange,
  [ActionType.UPDATE_GROWTH_HEADCOUNT]: updateHeadcount,
  [ActionType.UPDATE_WORKPLACE_STYLE_SELECTED]: updateWorkplaceStyleSelected,
  [ActionType.UPDATE_SPACES]: updateSpaces,
  [ActionType.UPDATE_CUSTOM_SPACES]: updateCustomSpaces,
  [ActionType.UPDATE_LAST_STEP]: updateLastStep,
  [ActionType.UPDATE_SCORECARD_REACHED]: updateScorecardReached,
  [ActionType.REMOVE_CUSTOM_SPACES]: removeCustomSpaces,
  [ActionType.SET_NA]: setNA,
  [ActionType.RESET_NA]: resetNA,
  [ActionType.UPDATE_SPACE_SETTINGS]: updateSpaceSettings,
  [ActionType.ADD_SHARED_USER]: addSharedUser,
  [ActionType.REMOVE_SHARED_USER]: removeSharedUser,
  [ActionType.SET_ANYONE_CAN_ACCESS]: setAnyoneCanAccess,
  [ActionType.UPDATE_RATIOS]: updateRatios,
};

const reducer = (state = initialState(), action) => storeReducer(actionsMapping, state, action);

export default reducer;
